/**
 * useHydra — React hook that manages the HydraClient lifecycle.
 * Starts on mount, stops on unmount. Feeds data into the Zustand marketStore.
 */

import { useEffect, useRef } from 'react';
import { HydraClient } from '../clients/HydraClient';
import { useMarketStore } from '../stores/marketStore';

/**
 * Hydra consumer WS lives on the API port (8891), not on :443 — the public site is the Next dashboard.
 * Local Vite is http://localhost, so `ws://hydra.newbx.com:8891/ws` is allowed (no mixed content).
 * HTTPS deployments need WSS (env or a terminator that proxies to :8891); see `.env.example`.
 * Bearer auth on the consumer socket is not available from the browser; see `HydraClient`.
 */
function defaultHydraWsUrl(): string {
  if (import.meta.env.DEV) {
    return 'ws://hydra.newbx.com:8891/ws';
  }
  return 'wss://hydra.newbx.com/ws';
}

const HYDRA_URL = (import.meta.env.VITE_HYDRA_URL as string | undefined)?.trim() || defaultHydraWsUrl();

export function useHydra() {
  const clientRef = useRef<HydraClient | null>(null);
  const connected = useMarketStore((s) => s.connected);
  const marketsCount = useMarketStore((s) => s.marketsCount);
  const orderbooksCount = useMarketStore((s) => s.orderbooksCount);

  useEffect(() => {
    const client = new HydraClient(HYDRA_URL, {
      onMarket: (market) => useMarketStore.getState().ingestMarket(market),
      onOrderbook: (ticker, book) => useMarketStore.getState().ingestOrderbook(ticker, book),
      onStatus: (isConnected) => useMarketStore.getState().setConnected(isConnected),
    });

    clientRef.current = client;
    useMarketStore.getState().setClient(client);
    client.start();

    return () => {
      useMarketStore.getState().setClient(null);
      client.stop();
      clientRef.current = null;
    };
  }, []);

  return { connected, marketsCount, orderbooksCount };
}
